/** Points-to-screen vertex shader. */

precision highp float;

#define positionChannels points_channels_0
#define massChannels points_channels_2

points_useSamples
points_useReads_position

attribute float index;

#ifdef points_splits
  uniform sampler2D points_states[points_splits];
#else
  uniform sampler2D points_states;
#endif

uniform float points_stepNow;
uniform vec4 points_stateShape;

uniform float wideHi;
uniform float wideLo;
uniform vec2 widths;
uniform vec4 colorHi;
uniform vec4 colorLo;

#ifdef colorImage
  uniform sampler2D colorImage;
  uniform vec2 colorBy;
  uniform vec2 flip;
  uniform vec4 offset;
  uniform vec2 aspectImage;
#endif

uniform float dt;
uniform vec2 size;
uniform vec2 aspectScreen;
uniform vec4 bounds;
uniform float form;

#ifdef rescale_mass
  uniform vec2 rescaleMass;
#else
  const float rescaledMass = 1.0;
#endif
#ifdef rescale_position
  uniform vec2 rescalePosition;
#else
  const float rescaledPosition = 1.0;
#endif

varying float wide;
varying vec3 sphere;
varying vec4 color;

#pragma glslify: map = require(glsl-map);
#pragma glslify: neq = require(glsl-conditionals/when_neq);

#ifdef colorImage
  #pragma glslify: ge = require(glsl-conditionals/when_ge);
#endif

#pragma glslify: indexUV = require(@epok.tech/gl-gpgpu/src/lookup/index-uv);
#pragma glslify: offsetUV = require(@epok.tech/gl-gpgpu/src/lookup/offset-uv);

#if defined(rescale_mass) || defined(rescale_position)
  #pragma glslify: exponent = require(../vector/exponent);
#endif

#if points_stepsPast > 1
  #define indexFormsStates
  #ifdef indexFormsStates
    #pragma glslify: indexStates = require(@epok.tech/gl-gpgpu/src/index-forms/index-states)
  #else
    #pragma glslify: indexEntries = require(@epok.tech/gl-gpgpu/src/index-forms/index-entries)
  #endif
#endif

const vec2 v1 = vec2(1);
const vec2 v0 = v1*0.0;
const vec4 hidden = vec4(0, 0, 0, -1);

#ifdef colorImage
  const float colorByBase = 1.0;
  const float colorByImage = 2.0;
#endif

void main() {
  #if points_stepsPast > 1
    // If multiple steps are given, find past step and entry.
    // Lookups mostly equivalent; input and result iteration order differ.
    #ifdef indexFormsStates
      vec2 stepEntry = indexStates(index, points_stepsPast, form);
    #else
      vec2 stepEntry = indexEntries(index, points_entries, form);
    #endif

    float stepPast = stepEntry.s;
    float entry = stepEntry.t;
  #else
    // If only 1 step is given, past step and entry are known.
    float stepPast = 0.0;
    float entry = index;
  #endif

  vec2 st =
    offsetUV(indexUV(entry, points_stateShape.xy), points_stateShape.xy);
  // vec2 st = indexUV(entry, points_stateShape.xy);

  #if points_stepsPast > 1
    points_tapStateBy(st, stepPast, 0);
  #else
    points_tapState(st);
  #endif

  #ifdef rescale_mass
    float rescaledMass = exponent(rescaleMass);
  #endif
  #ifdef rescale_position
    float rescaledPosition = exponent(rescalePosition);
  #endif

  float mass = rescaledMass*
    points_data[points_reads_position_mass].massChannels;

  vec2 position = rescaledPosition*
    points_data[points_reads_position_position].positionChannels;

  float show = neq(mass, 0.0);
  vec2 boundSize = bounds.zw-bounds.xy;
  vec2 to = (position/max(boundSize.x, boundSize.y))*2.0;
  vec4 screen = mix(hidden, vec4(to, 0, 1), show);

  screen.xy *= aspectScreen;
  gl_Position = screen;

  float ago = stepPast/max(float(points_stepsPast-1), 1.0);

  color = mix(colorHi, colorLo, ago);

  #ifdef colorImage
    vec2 pq = ((fract(st*flip)*(aspectImage/aspectScreen))/offset.zw)-offset.xy;
    vec4 colorPixel = texture2D(colorImage, pq);
    float colorAt = colorBy.x;

    colorAt = mix(colorAt, (1.0-color.a)+colorBy.x, ge(colorBy.y, colorByBase));
    colorAt = mix(colorAt, colorPixel.a+colorBy.x, ge(colorBy.y, colorByImage));
    color = mix(color, colorPixel, clamp(colorAt, 0.0, 1.0));
  #endif

  color *= show;

  wide = mix(wideHi, wideLo, ago)*show;

  float pointSize = gl_PointSize = 0.5*clamp(wide/screen.w, widths.s, widths.t);

  /**
   * Convert vertex position to `gl_FragCoord` window-space.
   * @see [SO](https://stackoverflow.com/a/7158573)
   * @see [SO](https://stackoverflow.com/a/54237532/716898)
   * @todo Why does the correct size here seem to be half the radius?
   */
  sphere = vec3(size*map(screen.xy/screen.w, -v1, v1, v0, v1), pointSize*0.5);
}
